import { useEffect } from 'react';

import { getActionAriaLabel } from '@/utils/urlUtils';

import { Box } from '@/components/Box';
import { generateDataTestId } from '@/lib/tests';
import type { Maybe, WithChildren } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';

import type { Gallery } from '../context';
import { useCompoundContext } from '../context';

import * as GS from '../ImageCarousel.styles';

export interface CarouselPreviewItemProps extends WithChildren {
  height?: Maybe<string | number>;
  index?: number;
  largeSrc?: Maybe<string>;
  width?: Maybe<string | number>;
}

export const CarouselItem = ({
  children,
  height,
  index,
  largeSrc,
  width,
}: CarouselPreviewItemProps) => {
  const { gallery, isProcessing } = useCompoundContext();
  const { locale } = useRegionProvider();

  useEffect(() => {
    if (gallery && !isProcessing && (!height || !largeSrc || !width)) {
      throw new Error(
        'When gallery prop is passed - height, largeSrc and width must be provided',
      );
    }
  }, [gallery, height, largeSrc, width]);

  if (shouldRenderGallery({ gallery, height, isProcessing, largeSrc, width })) {
    return (
      <GS.PhotoSwipeLink
        aria-label={
          getActionAriaLabel('view_gallery_image', locale.code) ||
          'View full size image in gallery'
        }
        data-pswp-height={height}
        data-pswp-index={index}
        data-pswp-width={width}
        data-test-id='CarouselItemPhotoSwipeLink'
        href={largeSrc ?? '#'}
        isProcessing={isProcessing}
        onClick={(evt) => {
          if (isProcessing) {
            evt.preventDefault();
            evt.stopPropagation();
          }
        }}
        rel='noopener noreferrer'
        target='_blank'
      >
        <Box
          {...generateDataTestId('imagesGallery', 'carouselItemWithGallery')}
          data-test-id='CarouselItemBox'
        >
          {children}
        </Box>
      </GS.PhotoSwipeLink>
    );
  } else {
    return (
      <Box
        {...generateDataTestId('imagesGallery', 'carouselItemWithoutGallery')}
        data-test-id='CarouselItemBox'
      >
        {children}
      </Box>
    );
  }
};

interface ShouldRenderGalleryProps {
  gallery: Gallery | undefined;
  height: Maybe<string | number>;
  isProcessing: boolean;
  largeSrc: Maybe<string>;
  width: Maybe<string | number>;
}

const shouldRenderGallery = ({
  gallery,
  height,
  isProcessing,
  largeSrc,
  width,
}: ShouldRenderGalleryProps): boolean =>
  !!gallery && !!height && !isProcessing && !!largeSrc && !!width;
